import { useGo, useResourceParams } from '@refinedev/core';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useResetQueriesOnContextChange } from '@/hooks/useResetQueriesOnContextChange';

import { useUserContext } from './UserContext';

let selectedCountryIdSnapshot: number | undefined;

export const getSelectedCountryId = () => selectedCountryIdSnapshot;

const selectedCountryIdStorageKey = 'selectedCountryId';
const getStoredSelectedCountryId = () => {
  if (typeof window === 'undefined') return undefined;

  const storedCountryId = window.localStorage.getItem(selectedCountryIdStorageKey);
  const parsedCountryId = Number(storedCountryId);

  return storedCountryId !== null && (parsedCountryId === 0 || parsedCountryId === 1)
    ? parsedCountryId
    : undefined;
};

const storeSelectedCountryId = (countryId?: number) => {
  if (typeof window === 'undefined' || countryId === undefined) return;

  window.localStorage.setItem(selectedCountryIdStorageKey, countryId.toString());
};

interface CountryContextType {
  selectedCountryId?: number;
  setSelectedCountryId: (countryId?: number) => void;
  isBrasilSelected: boolean;
}

export const COUNTRY = {
  ARGENTINA: 1,
  BRASIL: 3,
};

const CountryContext = createContext<CountryContextType>({} as CountryContextType);

export const CountryProvider = ({ children }: { children: ReactNode }) => {
  const { userLogged } = useUserContext();
  const { action, resource } = useResourceParams();
  const go = useGo();
  const {
    clearInactiveQueriesOnContextChange,
    prepareQueriesForContextChange,
    resetQueriesOnContextChange,
  } = useResetQueriesOnContextChange();
  const [selectedCountryId, setSelectedCountryIdState] = useState<number | undefined>();
  const pendingResetAfterNavigation = useRef(false);

  useEffect(() => {
    const storedCountryId = getStoredSelectedCountryId();
    const initialCountryId =
      storedCountryId ?? (userLogged?.companyData?.pais?.id === COUNTRY.BRASIL ? 1 : 0);

    // Brasil tiene id 3 en la base de datos pero el flag de la API es booleano, por lo que se mapea a 1 para mantener compatibilidad con el frontend.
    selectedCountryIdSnapshot = initialCountryId;
    setSelectedCountryIdState(initialCountryId);
  }, [userLogged?.companyData?.pais]);

  const isBrasilSelected = selectedCountryId === 1;

  useEffect(() => {
    if (!pendingResetAfterNavigation.current || action !== 'list') return;

    pendingResetAfterNavigation.current = false;
    clearInactiveQueriesOnContextChange();
  }, [action, clearInactiveQueriesOnContextChange]);

  const setSelectedCountryId = useCallback(
    (countryId?: number) => {
      if (countryId === selectedCountryId) return;

      const shouldNavigateToList = action && action !== 'list' && resource?.name;

      if (!shouldNavigateToList) {
        selectedCountryIdSnapshot = countryId;
        setSelectedCountryIdState(countryId);
        storeSelectedCountryId(countryId);

        // El país global no forma parte de la query key de Refine; resetear evita datos cacheados de otro país.
        void resetQueriesOnContextChange();

        return;
      }

      void prepareQueriesForContextChange().then(() => {
        selectedCountryIdSnapshot = countryId;
        setSelectedCountryIdState(countryId);
        storeSelectedCountryId(countryId);

        // Si el usuario se encuentra en la visualización de un elemento, se lo envía al listado para evitar errores por intentar mostrar datos de un país diferente al seleccionado.
        pendingResetAfterNavigation.current = true;
        go({ to: { resource: resource.name, action: 'list' }, type: 'replace' });
      });
    },
    [
      action,
      go,
      prepareQueriesForContextChange,
      resetQueriesOnContextChange,
      resource?.name,
      selectedCountryId,
    ],
  );

  return (
    <CountryContext.Provider value={{ selectedCountryId, setSelectedCountryId, isBrasilSelected }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountryContext = () => useContext(CountryContext);
